// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@rugby/XDL/types";
import { css } from "styled-components";

const fontPath = "/UserFiles/fonts";

// This is to be output as html in the head so
// fonts can load without blocking js rendering
export const fontsToLoad = () => {
    return {
        __html: `
        @font-face {
            font-family: "helvetica";
            src: url("${fontPath}/normal/helvetica-webfont.eot");
            src: url("${fontPath}/normal/helvetica-webfont.eot?#iefix") format("embedded-opentype"),
            url("${fontPath}/normal/helvetica-webfont.woff2") format("woff2"),
            url("${fontPath}/normal/helvetica-webfont.woff") format("woff"),
            url("${fontPath}/normal/helvetica-webfont.ttf") format("truetype"),
            url("${fontPath}/normal/helvetica-webfont.svg#helveticaregular") format("svg");
            font-weight: 400;
            font-style: normal;
            font-display: swap;
        }

        @font-face {
            font-family: "helvetica";
            src: url("${fontPath}/normal-oblique/helvetica-oblique-webfont.eot");
            src: url("${fontPath}/normal-oblique/helvetica-oblique-webfont.eot?#iefix") format("embedded-opentype"),
            url("${fontPath}/normal-oblique/helvetica-oblique-webfont.woff2") format("woff2"),
            url("${fontPath}/normal-oblique/helvetica-oblique-webfont.woff") format("woff"),
            url("${fontPath}/normal-oblique/helvetica-oblique-webfont.ttf") format("truetype"),
            url("${fontPath}/normal-oblique/helvetica-oblique-webfont.svg#helveticaoblique") format("svg");
            font-weight: 400;
            font-style: italic;
            font-display: swap;
        }

        /**
         * Bold, 600
         */
        @font-face {
            font-family: "helvetica";
            src: url("${fontPath}/bold/helvetica-bold-webfont.eot");
            src: url("${fontPath}/bold/helvetica-bold-webfont.eot?#iefix") format("embedded-opentype"),
            url("${fontPath}/bold/helvetica-bold-webfont.woff2") format("woff2"),
            url("${fontPath}/bold/helvetica-bold-webfont.woff") format("woff"),
            url("${fontPath}/bold/helvetica-bold-webfont.ttf") format("truetype"),
            url("${fontPath}/bold/helvetica-bold-webfont.svg#helveticabold") format("svg");
            font-weight: 600;
            font-style: normal;
            font-display: swap;
        }

        @font-face {
            font-family: "helvetica";
            src: url("${fontPath}/bold-oblique/helvetica-boldoblique-webfont.eot");
            src: url("${fontPath}/bold-oblique/helvetica-boldoblique-webfont.eot?#iefix") format("embedded-opentype"),
            url("${fontPath}/bold-oblique/helvetica-boldoblique-webfont.woff2") format("woff2"),
            url("${fontPath}/bold-oblique/helvetica-boldoblique-webfont.woff") format("woff"),
            url("${fontPath}/bold-oblique/helvetica-boldoblique-webfont.ttf") format("truetype"),
            url("${fontPath}/bold-oblique/helvetica-boldoblique-webfont.svg#helveticabold_oblique") format("svg");
            font-weight: 600;
            font-style: italic;
            font-display: swap;
        }

        /**
         * Light, 300
         */
        @font-face {
            font-family: "helvetica";
            src: url("${fontPath}/light/helvetica-light-webfont.eot");
            src: url("${fontPath}/light/helvetica-light-webfont.eot?#iefix") format("embedded-opentype"),
            url("${fontPath}/light/helvetica-light-webfont.woff2") format("woff2"),
            url("${fontPath}/light/helvetica-light-webfont.woff") format("woff"),
            url("${fontPath}/light/helvetica-light-webfont.ttf") format("truetype"),
            url("${fontPath}/light/helvetica-light-webfont.svg#helvetica_lightregular") format("svg");
            font-weight: 300;
            font-style: normal;
            font-display: swap;
        }

        @font-face {
            font-family: "helvetica";
            src: url("${fontPath}/light-oblique/helvetica-lightoblique-webfont.eot");
            src: url("${fontPath}/light-oblique/helvetica-lightoblique-webfont.eot?#iefix") format("embedded-opentype"),
            url("${fontPath}/light-oblique/helvetica-lightoblique-webfont.woff2") format("woff2"),
            url("${fontPath}/light-oblique/helvetica-lightoblique-webfont.woff") format("woff"),
            url("${fontPath}/light-oblique/helvetica-lightoblique-webfont.ttf") format("truetype"),
            url("${fontPath}/light-oblique/helvetica-lightoblique-webfont.svg#helveticalight_oblique") format("svg");
            font-weight: 300;
            font-style: italic;
            font-display: swap;
        }
        `,
    };
};

const fontConfig = {
    weight: {
        light: 400,
        regular: 400,
        bold: 700,
    },
    fontFamily: "'helvetica','Open Sans', 'Roboto', 'Lato', sans-serif",
};

export default {
    // fontFamilyImportUrl: "https://fonts.googleapis.com/css?family=Open+Sans:300,400,600,700,800&display=swap",
    body: {
        size: "15px",
        weight: 400,
        fontFamily: fontConfig.fontFamily,
    },
    p: {
        size: "15px",
        weight: 400,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    h1: {
        size: "46px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    h2: {
        size: "36px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    h3: {
        size: "32px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    h4: {
        size: "28px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    h5: {
        size: "22px",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    h6: {
        size: "18px",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 1rem;
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        fontFamily: fontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: fontConfig.fontFamily,
        transform: "uppercase",
        weight: 800,
    },
    headerTertiary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: fontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        color: "text.accent",
        transform: "uppercase",
        weight: 600,
    },
} as xBaseTheme["typography"];
